import React, { useState, useEffect } from 'react';
import { Star } from 'lucide-react';
import { storeStatsApi } from '../services/dealStatsApi';
import { toast } from 'sonner';

const StoreRatingInteractive = ({ storeSlug, initialRating = 0, initialVotes = 0 }) => {
  const [stats, setStats] = useState({
    average_rating: initialRating,
    total_ratings: initialVotes,
  });
  const [userRating, setUserRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch stats on mount
  useEffect(() => {
    const fetchStats = async () => {
      const data = await storeStatsApi.getStats(storeSlug);
      if (data) {
        setStats({
          average_rating: data.average_rating,
          total_ratings: data.total_ratings,
        });
      }
      setIsLoading(false);
    };
    fetchStats();

    // Check if user has rated this store
    const ratedStores = JSON.parse(localStorage.getItem('ratedStores') || '{}');
    if (ratedStores[storeSlug]) {
      setUserRating(ratedStores[storeSlug]);
    }
  }, [storeSlug]);

  const handleRate = async (rating) => {
    const ratedStores = JSON.parse(localStorage.getItem('ratedStores') || '{}');
    
    if (ratedStores[storeSlug]) {
      toast.info('You already rated this store!');
      return;
    }

    const data = await storeStatsApi.rateStore(storeSlug, rating);
    if (data) {
      setStats({
        average_rating: data.average_rating,
        total_ratings: data.total_ratings,
      });
      setUserRating(rating);
      ratedStores[storeSlug] = rating;
      localStorage.setItem('ratedStores', JSON.stringify(ratedStores));
      toast.success(`Rated ${rating} stars!`);
    }
  };

  const renderStars = () => {
    const stars = [];
    const displayRating = hoverRating || stats.average_rating;
    
    for (let i = 1; i <= 5; i++) {
      const isFilled = i <= Math.round(displayRating);
      const isHalf = !isFilled && i - 0.5 <= displayRating;
      
      stars.push(
        <button
          key={i}
          onClick={() => handleRate(i)}
          onMouseEnter={() => !userRating && setHoverRating(i)}
          onMouseLeave={() => setHoverRating(0)}
          className={`transition-all duration-150 ${userRating ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
          disabled={!!userRating}
        >
          <Star
            size={18}
            className={`${
              isFilled || (hoverRating && i <= hoverRating)
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-300'
            } transition-colors`}
          />
        </button>
      );
    }
    return stars;
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 animate-pulse">
        <div className="flex gap-0.5">
          {[...Array(5)].map((_, i) => (
            <Star key={i} size={18} className="text-gray-200" />
          ))}
        </div>
        <span className="w-16 h-4 bg-gray-200 rounded"></span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <div className="flex gap-0.5">
          {renderStars()}
        </div>
        <span className="text-gray-700 font-medium">
          {stats.average_rating.toFixed(2)}
        </span>
      </div>
      <span className="text-gray-500 text-sm">
        ({stats.total_ratings.toLocaleString()} {stats.total_ratings === 1 ? 'vote' : 'votes'})
      </span>
      {userRating > 0 && (
        <span className="text-red-600 text-xs">You rated: {userRating}★</span>
      )}
    </div>
  );
};

export default StoreRatingInteractive;
